import type {
  KambiSportsSameGameMultiSelection,
  KambiSportsSelection,
  KambiSportsSingleSelection,
} from '@mobi/betslip/types'
import {
  isKambiSportsMultiSelection,
  isKambiSportsSameGameMultiSelection,
  isKambiSportsSingleSelection,
} from '@mobi/betslip/helpers/typeGuards'
import type { KambiValidReward } from '@mobi/api-types/kambi/player/reward'
import { formatDollarDecimalToThousandMultiplier as convertToKambiSystemCurrency } from '@mobi/sports-offering/helpers/currency/currencyFormat'
import type { KambiBettingParams } from '@core/Data/Betting/kambiSports/types'

export function createKambiBetRequest({
  selection,
  selectedKambiReward,
  investmentWin,
}: CreateBetRequestParams): Pick<KambiBettingParams, 'bets' | 'couponRows' | 'rewards'> {
  if (isKambiSportsSingleSelection(selection)) {
    return {
      bets: [{ couponRowIndexes: [0], stake: convertToKambiSystemCurrency(investmentWin) }],
      couponRows: [buildSingleCouponRow(selection)],
      rewards: builtRewardsRequestPayload(selectedKambiReward),
    }
  }

  if (isKambiSportsSameGameMultiSelection(selection)) {
    return {
      bets: [{ couponRowIndexes: [0], stake: convertToKambiSystemCurrency(investmentWin) }],
      couponRows: [buildSameGameMultiCouponRow(selection)],
      rewards: builtRewardsRequestPayload(selectedKambiReward),
    }
  }

  if (isKambiSportsMultiSelection(selection)) {
    const { legs } = selection

    const couponRows: CouponRows = legs.map((leg, index) =>
      isKambiSportsSingleSelection(leg)
        ? buildSingleCouponRow(leg, index)
        : buildSameGameMultiCouponRow(leg, index)
    )

    return {
      bets: [
        {
          couponRowIndexes: couponRows.map((_, index) => index),
          stake: convertToKambiSystemCurrency(investmentWin),
        },
      ],
      couponRows,
      rewards: builtRewardsRequestPayload(selectedKambiReward),
    }
  }

  return { couponRows: [], bets: [] }
}

// =============
// Local Helpers
// =============

function buildSingleCouponRow(selection: KambiSportsSingleSelection, index = 0): CouponRows[0] {
  const { odds, id } = selection.betOffer.outcomes[0]
  return { index, odds, outcomeId: id, type: 'SIMPLE' }
}

function buildSameGameMultiCouponRow(
  selection: KambiSportsSameGameMultiSelection,
  index = 0
): CouponRows[0] {
  const { betOffers, odds } = selection
  return {
    index,
    odds,
    type: 'BET_BUILDER',
    group: {
      operation: 'AND',
      groups: betOffers.map(({ outcomes }) => ({ operation: 'AND', outcomeIds: [outcomes[0].id] })),
    },
  }
}

function builtRewardsRequestPayload(
  reward?: KambiValidReward | null
): KambiBettingParams['rewards'] {
  if (!reward) return []

  const { rewardType, rewardId, groupRewardId, profitBoostProperties } = reward

  if (rewardId) {
    return [
      {
        rewardType,
        rewardId,
        ...(profitBoostProperties && { boostPercentage: profitBoostProperties.boostPercentage }),
      },
    ]
  }

  if (groupRewardId) {
    return [
      {
        rewardType,
        groupRewardId,
        ...(profitBoostProperties && { boostPercentage: profitBoostProperties.boostPercentage }),
      },
    ]
  }

  return []
}

// =====
// Types
// =====

type CreateBetRequestParams = {
  investmentWin: number
  selection: KambiSportsSelection
  selectedKambiReward?: KambiValidReward | null
}

type CouponRows = KambiBettingParams['couponRows']
